import type { ReclamacionesSesion, Rol } from '@discopico/domain';
import { evaluarGuardian, type Exigencia } from '../../nucleo/autorizacion/guardian.js';
import { rutaInicialDe } from '../../nucleo/autorizacion/sesion.js';

/**
 * El mapa de la aplicación: qué pantallas existen y bajo qué grupo viven.
 *
 * LA EXIGENCIA DE CADA GRUPO ES LA MISMA QUE SU RUTA, COPIADA A PROPÓSITO DE UN SOLO LADO.
 * `Navegacion` la pasa por `evaluarGuardian`, que es la función que usan los `beforeLoad`
 * de `_operacion`, `_contabilidad`, `_direccion` y `_egresos`. Por eso el menú no puede
 * mostrar una puerta que el guardián vaya a cerrar: los dos le preguntan a lo mismo. Un
 * menú con su propia tabla de roles es un menú que tarde o temprano ofrece una pantalla
 * que rebota, y quien la pulsa cree que se rompió algo.
 *
 * NO ESTÁN `/ingresar` NI `/cambiar-password`: son las dos rutas públicas y se llega a
 * ellas por el guardián, no por el menú. Y tampoco `/`, que sólo redirige.
 */
/**
 * `exigencia` en un enlace es para la ruta que pide MÁS que su grupo, como la reapertura, que tiene
 * su propio `beforeLoad`: sin ella el operario vería una puerta que rebota.
 */
/**
 * `para` es la línea que la pantalla de aterrizaje del grupo pone debajo del nombre: para qué sirve,
 * dicho como lo diría el dueño del bar. El menú lateral no la muestra; la tarjeta sí, porque sin ella
 * las once tarjetas eran los mismos once nombres que ya están a la izquierda.
 */
export type Enlace = {
  readonly a: string;
  readonly texto: string;
  readonly para?: string;
  readonly exigencia?: Exigencia;
  /** La puerta de su ruta cuando no es la del grupo: `/efectivo` vive en Operación y se lista en Caja y banco. */
  readonly ruta?: Exigencia;
  /** Va dentro del enlace anterior que lo encabeza, con sangría: los pasos de la noche bajo «La noche». */
  readonly dentro?: true;
  /** Encabeza a los que van `dentro`, y se tiñe de coral para que se vea que los agrupa. */
  readonly encabeza?: true;
};

export type GrupoDeMenu = {
  readonly titulo: string;
  readonly exigencia: Exigencia;
  readonly enlaces: readonly Enlace[];
};

// Las cuatro puertas de ruta, una por grupo del árbol (`_operacion`, `_egresos`, `_contabilidad`, `_direccion`).
const OPERACION: Exigencia = { accion: 'jornada.abrirCerrar' };
const EGRESOS: Exigencia = { accion: 'gastoCajaMenor.registrar' };
const CONTABILIDAD: Exigencia = { accion: 'utilidad.ver', nivel: 3 };
const DIRECCION: Exigencia = { accion: 'usuario.administrar' };

/**
 * SEIS GRUPOS POR TAREA Y NO CINCO POR RUTA, pedido del dueño del negocio el 2026-10-06: la barra lateral no
 * se desplaza, y para eso ningún grupo puede pasar de nueve pantallas. Contabilidad tenía dieciocho. Un grupo
 * ya no coincide con un `beforeLoad`, así que el enlace que vive en otro árbol lleva su puerta en `ruta`.
 * Inicio de contabilidad y de dirección no están: son pantallas de aterrizaje y se llega por el logo.
 */
export const MAPA: readonly GrupoDeMenu[] = [
  {
    titulo: 'Operación',
    exigencia: OPERACION,
    enlaces: [
      // Los cuatro pasos de la noche van dentro de «La noche», como lo pidió el dueño del negocio el
      // 2026-10-03: abrir la noche y lo que sigue hasta el cierre se ve como una sola cosa.
      { a: '/operacion', texto: 'La noche', encabeza: true },
      { a: '/jornada/ventas', texto: 'Ventas y cover', dentro: true },
      { a: '/jornada/propinas', texto: 'Propinas y personal', dentro: true },
      { a: '/jornada/conteo', texto: 'Conteo de efectivo', dentro: true },
      { a: '/jornada/cierre', texto: 'Cierre de jornada', dentro: true },
      { a: '/jornada/noches', texto: 'Resumen de noches', exigencia: { accion: 'jornada.reabrir' } },
      { a: '/jornada/reapertura', texto: 'Reabrir una noche', exigencia: { accion: 'jornada.reabrir' } },
      { a: '/jornada/faltantes', texto: 'Faltantes' },
      { a: '/ventas-umashi', texto: 'Umashi' },
    ],
  },
  {
    titulo: 'Caja y banco',
    exigencia: CONTABILIDAD,
    enlaces: [
      { a: '/efectivo', texto: 'Efectivo', ruta: OPERACION },
      { a: '/caja-ahorro', texto: 'Entradas y salidas del efectivo', para: 'De dónde sale y a dónde va cada peso en efectivo del mes' },
      { a: '/cobros', texto: 'Cobros', para: 'Lo que le deben al bar: datáfono, plataformas y faltantes de empleados' },
      { a: '/tesoreria', texto: 'Tesorería', para: 'Dónde está el dinero hoy, cuenta por cuenta' },
      { a: '/banco/conciliacion', texto: 'Conciliación bancaria', para: 'El extracto del banco contra lo registrado' },
    ],
  },
  {
    titulo: 'Egresos',
    exigencia: EGRESOS,
    enlaces: [
      { a: '/egresos', texto: 'Registrar egreso' },
      { a: '/cortesias', texto: 'Cortesías' },
      { a: '/proveedores', texto: 'Proveedores', para: 'El directorio de a quién se le compra', ruta: CONTABILIDAD },
      { a: '/colaboradores', texto: 'Colaboradores', para: 'Quienes trabajan con el bar sin contrato: el personal de FYA, seguridad y los de por noche', ruta: CONTABILIDAD },
      { a: '/nomina', texto: 'Nómina y empleados', para: 'La liquidación del mes y los datos del personal', ruta: CONTABILIDAD },
    ],
  },
  // INVENTARIO, adjudicaciones 284 y 286. Lo hace el jefe de operarios, así que el grupo se abre con la misma
  // llave que la noche. Las compras se registran en Egresos: la categoría de insumos abre los productos.
  {
    titulo: 'Inventario',
    exigencia: OPERACION,
    enlaces: [
      { a: '/inventario/conteo', texto: 'Conteo de inventario', para: 'Contar lo que hay en la bodega' },
      { a: '/compras/validacion', texto: 'Validación de compras', para: 'Aprobar las compras antes de que entren al inventario', exigencia: DIRECCION },
      { a: '/catalogo', texto: 'Catálogo', para: 'Productos e insumos del bar y cuánto hay de cada uno', exigencia: DIRECCION },
      { a: '/costeo-receta', texto: 'Costeo por receta', para: 'Cuánto cuesta preparar cada plato y cuánto deja', ruta: DIRECCION },
    ],
  },
  {
    titulo: 'Informes',
    exigencia: CONTABILIDAD,
    enlaces: [
      { a: '/tablero', texto: 'Tablero', para: 'Ventas, gastos y utilidad del mes, con las alertas abiertas', ruta: DIRECCION },
      { a: '/resultados', texto: 'Estado de resultados', para: 'Ventas, costos, gastos y utilidad del mes' },
      { a: '/punto-equilibrio', texto: 'Punto de equilibrio', para: 'Cuánto hay que vender en el mes para no perder', ruta: DIRECCION },
      { a: '/pasivos-noche', texto: 'Pasivos de la noche', para: 'Propinas e impoconsumo que el bar debe' },
      { a: '/impuestos-compras', texto: 'Impuestos de las compras', para: 'IVA, impuesto al consumo y retenciones de las facturas, según Siigo' },
      { a: '/cortesias/mes', texto: 'Cortesías del mes', para: 'Lo regalado y lo consumido por el equipo, a precio de carta' },
      { a: '/anulaciones', texto: 'Anulaciones', para: 'Los movimientos anulados del mes y su motivo' },
      { a: '/libro-movimientos', texto: 'Libro de movimientos', para: 'Cada peso que entró o salió, en orden' },
      { a: '/cierre-periodo', texto: 'Cierre de periodo', para: 'Cerrar el mes para que nadie lo cambie después' },
    ],
  },
  {
    titulo: 'Ajustes',
    exigencia: DIRECCION,
    enlaces: [
      { a: '/dias-apertura', texto: 'Días de apertura', para: 'Qué días abre el bar, y cerrar o abrir un día puntual' },
      { a: '/parametros-caja', texto: 'Parámetros de caja', para: 'La base de la noche y el fondo de la caja menor' },
      { a: '/gastos-fijos', texto: 'Gastos fijos', para: 'Arriendo, servicios y demás pagos que se repiten cada mes' },
      { a: '/categorias', texto: 'Categorías', para: 'Las categorías de ingreso y de gasto, y cómo se clasifica cada una', ruta: CONTABILIDAD },
      { a: '/umashi', texto: 'Precios de Umashi', para: 'El precio a publicar en las plataformas de domicilio y sus pedidos' },
      { a: '/socios', texto: 'Socios', para: 'Aportes y retiros de cada socio' },
      { a: '/saldos-apertura', texto: 'Saldos de apertura', para: 'Con cuánto arrancó cada cuenta al empezar a usar el sistema' },
      { a: '/usuarios', texto: 'Usuarios', para: 'Quién entra a la aplicación y con qué rol' },
    ],
  },
];

/**
 * Los grupos que esta sesión puede abrir, con la MISMA pregunta que hace cada `beforeLoad`.
 *
 * Una sesión con la contraseña temporal pendiente no ve ninguno, y es lo correcto: el guardián
 * la manda a `/cambiar-password` desde cualquier grupo, así que un menú lleno sería un menú de
 * puertas que rebotan todas al mismo sitio.
 */
export function gruposVisibles(sesion: ReclamacionesSesion | null): GrupoDeMenu[] {
  const puede = (e: Exigencia) => evaluarGuardian(sesion, e).permitido;
  return MAPA.map((g) => ({
    ...g,
    enlaces: g.enlaces.filter((e) => puede(e.ruta ?? g.exigencia) && (!e.exigencia || puede(e.exigencia))),
  })).filter((g) => g.enlaces.length > 0);
}

export type Destino = { readonly a: string; readonly texto: 'Inicio' | 'Jornada' | 'Egresos' };

/**
 * Los destinos fijos de la barra inferior del teléfono, pantalla 1a del diseño: Inicio, Jornada y
 * Egresos, y después «Más», que abre el menú completo y no es un destino.
 *
 * JORNADA Y EGRESOS SALEN DE LOS MISMOS GRUPOS DEL MAPA, así que la barra tampoco puede ofrecer una
 * puerta que el guardián cierre: el contador no ve «Jornada» porque no ve el grupo Operación.
 *
 * Para quien aterriza en `/operacion` (operario y jefe), Inicio YA ES la noche, así que Jornada
 * lleva al conteo, que es el acto central de la jornada. Para el administrador, que aterriza en
 * el tablero, Jornada lleva a la noche. Sin esa bifurcación el operario tendría dos pestañas que
 * abren lo mismo.
 */
export function destinosDeBarra(sesion: ReclamacionesSesion | null): Destino[] {
  if (!sesion) return [];
  const visibles = new Set(gruposVisibles(sesion).map((g) => g.titulo));
  if (visibles.size === 0) return [];
  const inicio = rutaInicialDe(sesion.rol);
  const destinos: Destino[] = [{ a: inicio, texto: 'Inicio' }];
  if (visibles.has('Operación')) {
    destinos.push({ a: inicio === '/operacion' ? '/jornada/conteo' : '/operacion', texto: 'Jornada' });
  }
  if (visibles.has('Egresos')) destinos.push({ a: '/egresos', texto: 'Egresos' });
  return destinos;
}

export const ETIQUETA_ROL: Record<Rol, string> = {
  operario: 'Operario',
  jefe_operarios: 'Jefe de operarios',
  contador: 'Contador',
  administrador: 'Administrador',
};
